import { Controller } from '@hotwired/stimulus'

const MINUTES_PROMPT = `
Your job is to read rough meeting notes for a church member meeting and process them into a draft of meeting minutes.

Stick to a formal writing style, and keep the minutes concise. Not everything in the raw notes needs to make it to the final document. Since this is a church member meeting, religious references are appropriate and can generally be preserved.

The church (Providence Baptist Church) generally holds to the 1689 baptist confession, with some minor disagreements, and reformed doctrine in general.
Discussions of Baptism, Communion (or The Lord's Supper), and other church-related topics should be interpret accordingly (i.e., NOT using the Presbyterian or Roman Catholic views).

You will be provided XML data in this format:
<raw_notes>
Raw notes in markdown format
</raw_notes>

<meeting>
<section title="Section Title">
Some section contents

<section title="Subsection Title">
Subsection contents
</section>
</section>
</meeting>

The data may or may not have indentation. Sections and subsections can be nested to arbitrary depth. Sections can have both content and subsections, but their content always precedes any subsections. Section content can be formatted as markdown.

You will output the "<meeting>" subtree in the same format you are given. Output ONLY the XML structure and no other text around it. Make sure to use HTML entities (such as &amp; / &gt; / &lt;) where appropriate. Both the raw_notes and meeting may contain text. Pull primarily from raw_notes and use the meeting tree for the outline.

IMPORTANT: REPRODUCE THE TITLES AND HIERARCHY OF SECTIONS EXACTLY IN YOUR RESPONSE. DO NOT ALTER IT IN ANY WAY.

Change ONLY the section contents to turn the rough notes into meeting minutes. If a section would have no contents DO NOT REMOVE THAT SECTION. Instead, use "None" as its contents.

Remember that these are rough notes. They may include things that do not need to make it to the final minutes. Make sure the draft you create is accurate to the raw notes. Do not supply additional details that are not in the raw notes.

Below is a short example:

EXAMPLE INPUT
<raw_notes>
Pastor Rob opened the meeting with prayer at 6:30pm.

Rob read Psalm 81.

Rob corrected previous statement of "monthly member meetings". Meetings are quarterly. We've adjusted the schedule starting with this meeting to be at the start of the quarter instead of one month in.

**Membership**

No admissions. Zaroveys are being recommended for membership. The date of the vote will be the 12th or 19th.

No dismissals, no baptisms.

Calendar reminder: baptism scheduled for Sunday (Talitha Borstad) and potluck lunch at Riverview Park, as well as the Lord's Supper. There are not a lot of power outlets, so plan accordingly.

**Treasury**
Nate Borstad gave the treasury update

Balances as of 5/31

Checking: $16,349.16
Savings: $53,711.95
CDs: $805,851.72

For CDs, we put 700k in in early '23. It's grown to 105k which is great.

None

Rob: Anything we need to bring to the deacons' attention?

Rob closed the meeting with prayer at 7:02pm
</raw_notes>

<meeting>
  <section title="Invocation &amp; Scripture">
    Pastor Rob opened the meeting with prayer at 6:30pm.

    Rob read Psalm 81.
  </section>

  <section title="Elder Reports">
    <section title="Membership">
    </section>
  </section>

  <section title="Treasury">

  </section>

  <section title="Deacon Updates">

  </section>

  <section title="Benediction &amp; Prayer"
    Rob closed the meeting with prayer at 7:02pm
  </section>
</meeting>

EXAMPLE OUTPUT
<meeting>
  <section title="Invocation &amp; Scripture">
    Pastor Rob opened the meeting with prayer at 6:30pm and read Psalm 81.
  </section>

  <section title="Elder Reports">
    Rob corrected a previous statement about having monthly member meetings. Member meetings are held quarterly. The schedule has been adjusted to be at the beginning of the quarter instead of one month in. This meeting is the first on the new schedule.

    <section title="Membership">
      There were no admissions, dismissals, or baptisms since the last meeting.

      The elders are recommending the Zaroveys for membership. The vote will take place on either the 12th or 19th.

      Pastor Rob reminded the congregation that we will be celebrating the baptism of Talitha Borstad at Riverview Park this Sunday, as well as the Lord's Supper. Rob advised everyone to be aware of the limited number of power outlets at the park.
    </section>
  </section>

  <section title="Treasury">
    Nathan Borstad gave a budget snapshot

    Account balances as of 5/31:
    - Checking: $16,349.16
    - Savings: $53,711.95
    - CDs: $805,851.72

    Nathan explained that we put around $700,000 into CDs in early 2023. Those have since grown by $105,000.
  </section>

  <section title="Deacon Updates">
    None
  </section>

  <section title="Benediction &amp; Prayer">
    Rob closed the meeting with prayer at 7:02pm.
  </section>
</meeting>
`

export default class AIAssistantController extends Controller {
  static targets = ['endpoint', 'apiKey', 'model', 'apply', 'liveOutput']

  static outlets = ['raw-notes-content', 'draft-content']

  #isProcessing = false
  #abortController = null

  connect() {
    this.#readLocalStorage()
  }

  #readLocalStorage() {
    const localStorageString
      = localStorage.getItem('a-la-minute.ai-assistant') || '{}'
    let localStorageData = {}
    try {
      localStorageData = JSON.parse(localStorageString)
    }
    catch {}

    const { endpoint, apiKey, model } = localStorageData

    this.endpointTarget.value = endpoint ?? ''
    this.apiKeyTarget.value = apiKey ?? ''
    this.modelTarget.value = model ?? ''

    this.#updateEnabledButtons()
  }

  fieldChanged() {
    const localStorageData = {
      endpoint: this.endpointTarget.value,
      apiKey: this.apiKeyTarget.value,
      model: this.modelTarget.value,
    }

    localStorage.setItem(
      'a-la-minute.ai-assistant',
      JSON.stringify(localStorageData),
    )
    this.#updateEnabledButtons()
  }

  async apply() {
    if (this.#isProcessing) return

    const baseEndpoint = this.endpointTarget.value
    const apiKey = this.apiKeyTarget.value
    const model = this.modelTarget.value
    const url = `${baseEndpoint}/chat/completions`

    this.#isProcessing = true
    this.applyTarget.disabled = true
    this.applyTarget.textContent = 'Processing...'
    this.liveOutputTarget.value = ''
    this.liveOutputTarget.style.display = 'block'
    this.#abortController = new AbortController()

    try {
      const rawNotes = await this.rawNotesContentOutlet.getContent()
      const draftContent = await this.draftContentOutlet.getContent()
      const input = `<raw_notes>\n${rawNotes}\n</raw_notes>\n\n${draftContent}`

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`,
          'Accept': 'text/event-stream',
        },
        body: JSON.stringify({
          model,
          stream: true,
          messages: [
            {
              role: 'system',
              content: MINUTES_PROMPT,
            },
            {
              role: 'user',
              content: input,
            },
          ],
        }),
        signal: this.#abortController.signal,
      })

      let aiContent = ''
      let thinkingContent = ''
      let hasThinking = false
      const reader = response.body.getReader()
      const decoder = new TextDecoder()

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        const chunk = decoder.decode(value, { stream: true })
        const lines = chunk
          .split('\n')
          .filter(line => line.startsWith('data: '))

        for (const line of lines) {
          const data = line.slice(6)
          if (data === '[DONE]') continue

          try {
            const parsed = JSON.parse(data)
            const delta = parsed.choices?.[0]?.delta
            if (!delta) continue

            const content = delta.content
            if (content) {
              aiContent += content
              this.liveOutputTarget.value = aiContent
              this.liveOutputTarget.scrollTop = this.liveOutputTarget.scrollHeight
            }

            // Capture reasoning/thinking content if present
            const reasoning = delta.reasoning_content
            if (reasoning) {
              hasThinking = true
              thinkingContent += reasoning
              this.liveOutputTarget.value
                = aiContent
                  + '\n\n<details open>\n<summary>Model Thinking</summary>\n<pre>'
                  + thinkingContent.replace(/</g, '&lt;').replace(/>/g, '&gt;')
                  + '\n</pre>\n</details>\n'
              this.liveOutputTarget.scrollTop = this.liveOutputTarget.scrollHeight
            }
          }
          catch {}
        }
      }

      const finalContent = hasThinking
        ? aiContent
        + '\n\n---\n\n<details open>\n<summary>Model Thinking</summary>\n<pre>'
        + thinkingContent.replace(/</g, '&lt;').replace(/>/g, '&gt;')
        + '\n</pre>\n</details>'
        : aiContent
      await this.draftContentOutlet.updateContent(finalContent)
    }
    finally {
      this.#isProcessing = false
      this.#abortController = null
      this.#updateEnabledButtons()
    }
  }

  cancel() {
    if (this.#abortController) {
      this.#abortController.abort()
      this.#isProcessing = false
      this.applyTarget.disabled = false
      this.applyTarget.textContent = 'Apply'
      this.liveOutputTarget.style.display = 'none'
    }
  }

  #updateEnabledButtons() {
    const configured
      = this.#endpointSet && this.apiKeyTarget.value && this.modelTarget.value
    const processing = this.#isProcessing

    this.applyTarget.disabled = !configured || processing
    this.applyTarget.textContent = processing ? 'Processing...' : 'Apply'
  }

  get #endpointSet() {
    const value = this.endpointTarget.value
    return /^https?:\/\//.test(value) && value.endsWith('/v1')
  }
}
